import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Users, UserCheck, MessageSquare, PhoneCall, Loader2 } from "lucide-react";
import { Button } from "../ui/button";

/**
 * RC-WEB-4 §23 — "Destinatarios de avisos" replaces the legacy
 * "Información de contacto / Contacto principal / Emergencia (scanner)" block.
 * The scanner never sees a phone number: routing is owner + trusted contacts.
 */

export const RecipientsCard = ({ profileId }) => {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profileId) return;
    let alive = true;
    axios.get(`/api/routing/profile/${profileId}`)
      .then((r) => { if (alive) setGroup(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [profileId]);

  const members = (kind) => {
    const list = group?.[kind === "call" ? "call_group" : "message_group"]
      || group?.[kind === "call" ? "calls" : "messages"] || [];
    return Array.isArray(list) ? list : [];
  };

  const label = (m) =>
    m.role === "owner" ? "Tú (dueño)" : (m.label || m.public_label || "Contacto de confianza");

  const row = (icon, title, list, testid) => {
    const Icon = icon;
    return (
      <div className="rounded-xl border border-slate-200 p-4" data-testid={testid}>
        <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#0B2545]">
          <Icon className="h-4 w-4 text-[#1B84FF]" /> {title}
        </p>
        <ul className="space-y-1.5 text-sm text-slate-700">
          {list.length === 0 ? (
            <li className="text-slate-600">Solo tú recibes estos avisos.</li>
          ) : (
            list.map((m, i) => (
              <li key={`${m.ref || m.role}-${i}`} className="flex items-center gap-2">
                <UserCheck className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
                {label(m)}
              </li>
            ))
          )}
        </ul>
      </div>
    );
  };

  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 sm:p-6" data-testid="recipients-card">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-[#0B2545]" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Users className="h-5 w-5 text-[#1B84FF]" /> Destinatarios de avisos
          </h3>
          <p className="text-sm text-slate-600">
            Quién recibe los mensajes y las llamadas de este QR. Nadie ve ni comparte números de teléfono.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#1B84FF]" /></div>
      ) : (
        <div className="grid min-w-0 gap-3 sm:grid-cols-2">
          {row(MessageSquare, "Mensajes", members("message"), "recipients-messages")}
          {row(PhoneCall, "Llamadas", members("call"), "recipients-calls")}
        </div>
      )}

      <Link to="/contactos-de-confianza">
        <Button variant="outline" className="mt-4 w-full border-slate-300 text-[#0B2545]" data-testid="manage-trusted-contacts-btn">
          Gestionar contactos de confianza
        </Button>
      </Link>
    </div>
  );
};

export default RecipientsCard;
